import React from 'react';
import styles from './ImageCard.module.scss';
import { ImageCardFragment } from '@src/graphql/gql-types';
import MediaImage from '../MediaImage';
import { getCTA } from '@src/utils/url';
import classNames from 'classnames';
import LinkWrapper from '../LinkWrapper';
import BoxIcon from '@src/elements/BoxIcon';
import {
  COLOR_SIDECAR_LIGHTER,
  COLOR_WHITE,
  ColorSidecarLighter,
  ColorWhite
} from '@src/types/colors';

type ImageCard = ImageCardFragment & {
  background?: ColorWhite | ColorSidecarLighter;
  fill?: boolean;
  collapsibleDescription?: boolean;
};

const ImageCardContent: React.FC<
  Pick<
    ImageCard,
    | 'image'
    | 'heading'
    | 'description'
    | 'primaryCta'
    | 'collapsibleDescription'
  >
> = ({
  image,
  heading,
  description,
  primaryCta,
  collapsibleDescription = false
}) => (
  <>
    <div className={styles.imageCardImage}>
      {image && <MediaImage {...image} />}
    </div>
    <div className={styles.imageCardContent}>
      <div>
        <h4 className={styles.imageCardHeading}>{heading}</h4>
        <p
          className={classNames(styles.imageCardDescription, {
            [styles.imageCardDescriptionCollapsible]: collapsibleDescription
          })}
        >
          {description}
        </p>
      </div>

      {primaryCta && (
        <p className={styles.imageCardLink}>
          {primaryCta.label}
          <BoxIcon name="right-arrow-alt" size="sm" />
        </p>
      )}
    </div>
  </>
);

const ImageCard: React.FC<ImageCard> = ({
  image,
  heading,
  description,
  primaryCta,
  background = COLOR_WHITE,
  fill = false,
  collapsibleDescription = false
}) => {
  return (
    <div
      className={classNames(styles.imageCard, {
        [styles.imageCardSidecar]: background === COLOR_SIDECAR_LIGHTER,
        [styles.imageCardFill]: fill
      })}
    >
      {primaryCta ? (
        <LinkWrapper {...getCTA(primaryCta)}>
          <ImageCardContent
            image={image}
            heading={heading}
            description={description}
            primaryCta={primaryCta}
            collapsibleDescription={collapsibleDescription}
          />
        </LinkWrapper>
      ) : (
        <ImageCardContent
          image={image}
          heading={heading}
          description={description}
          collapsibleDescription={collapsibleDescription}
        />
      )}
    </div>
  );
};

export default ImageCard;
